
import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, Card, Paper, Divider, Alert, CircularProgress,
  Chip, Button, Pagination, Stack, FormControl, Select, MenuItem,
  Drawer, IconButton, TextField, InputLabel, FormControlLabel, Switch,
  useMediaQuery, useTheme, alpha,
} from "@mui/material";
import {
  AccessTime, CalendarToday, Launch, PersonOutline,
  Add, Close, VideoCall as VideoIcon, Link as LinkIcon,
  Notes as NotesIcon, Title as TitleIcon, Timer as TimerIcon,
  NotificationsActive as NotifyIcon, EventNote, Refresh,
} from "@mui/icons-material";
import axios from "axios";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
};

const API = "https://backend-global-1vq9.onrender.com";
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const DEFAULT_PAGE_SIZE = 5;
const PLATFORMS = ["Zoom", "Google Meet", "Microsoft Teams", "Physical Meeting"];

// ── Helpers ──────────────────────────────────────────────────────────────────
const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const isSessionStillActive = (session) => {
  if (!session?.date) return false;
  const time = session.endTime || "23:59";
  const sessionEnd = new Date(`${session.date}T${time}`);
  if (isNaN(sessionEnd.getTime())) return false;
  return sessionEnd.getTime() >= Date.now();
};

const initialForm = {
  title: "",
  date: "",
  startTime: "",
  endTime: "",
  duration: "",
  platform: "Zoom",
  meetingLink: "",
  purpose: "",
  notifyMentor: true,
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5, fontSize: 14,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.primary },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

// ── Create Session form (used inside the drawer) ─────────────────────────────
function CreateSessionForm({ mentorId, onCreated, onCancel }) {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const minDate = useMemo(() => todayLocalISO(), []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const validateFutureSession = ({ date, startTime, endTime }) => {
    if (!date) return "Please pick a session date.";
    const today = new Date(todayLocalISO() + "T00:00:00");
    const chosenDay = new Date(date + "T00:00:00");
    if (isNaN(chosenDay.getTime())) return "Invalid date.";
    if (chosenDay < today) return "Session date must be today or later.";
    if (startTime && endTime && endTime <= startTime) {
      return "End time must be after start time.";
    }
    if (date === todayLocalISO() && startTime) {
      const startDateTime = new Date(`${date}T${startTime}`);
      if (startDateTime.getTime() <= Date.now()) {
        return "For today's date, start time must be in the future.";
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const { title, date, startTime, endTime, duration, purpose } = form;

    if (!title || !date || !startTime || !endTime || !duration || !purpose) {
      setError("Please fill in all required fields.");
      return;
    }
    if (!mentorId) {
      setError("Mentor ID missing — please sign back in.");
      return;
    }

    const futureCheck = validateFutureSession({ date, startTime, endTime });
    if (futureCheck) { setError(futureCheck); return; }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await axios.post(
        `${API}/api/sessions`,
        { ...form, mentorId },
        { headers: { "Content-Type": "application/json" } }
      );
      setSuccess("Session created successfully!");
      setForm(initialForm);
      // Brief delay so the mentor sees the success state, then refresh + close
      setTimeout(() => { onCreated?.(); }, 700);
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.msg ||
        "Failed to create session. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Drawer header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5,
            background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <EventNote sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: C.textPrimary, lineHeight: 1.2 }}>
              New Session
            </Typography>
            <Typography sx={{ fontSize: 11, color: C.textMuted, fontWeight: 600 }}>
              Schedule a mentorship session
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={onCancel} sx={{ color: C.textMuted }}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <Divider sx={{ mb: 2.5, borderColor: C.borderSoft }} />

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }}>
          {success}
        </Alert>
      )}

      <Stack spacing={2}>
        <Box>
          <FieldLabel icon={<TitleIcon sx={{ fontSize: 14 }} />} label="Session Title *" />
          <TextField
            name="title" value={form.title} onChange={handleChange}
            fullWidth size="small" placeholder="e.g. React Hooks deep-dive"
            sx={fieldSx}
          />
        </Box>

        <Box>
          <FieldLabel icon={<CalendarToday sx={{ fontSize: 14 }} />} label="Date *" />
          <TextField
            name="date" type="date" value={form.date} onChange={handleChange}
            fullWidth size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minDate }}
            sx={fieldSx}
          />
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Box flex={1}>
            <FieldLabel icon={<AccessTime sx={{ fontSize: 14 }} />} label="Start *" />
            <TextField
              name="startTime" type="time" value={form.startTime} onChange={handleChange}
              fullWidth size="small" InputLabelProps={{ shrink: true }} sx={fieldSx}
            />
          </Box>
          <Box flex={1}>
            <FieldLabel icon={<AccessTime sx={{ fontSize: 14 }} />} label="End *" />
            <TextField
              name="endTime" type="time" value={form.endTime} onChange={handleChange}
              fullWidth size="small" InputLabelProps={{ shrink: true }} sx={fieldSx}
            />
          </Box>
        </Stack>

        <Box>
          <FieldLabel icon={<TimerIcon sx={{ fontSize: 14 }} />} label="Duration *" />
          <TextField
            name="duration" value={form.duration} onChange={handleChange}
            placeholder="e.g. 60 minutes"
            fullWidth size="small" sx={fieldSx}
          />
        </Box>

        <Box>
          <FieldLabel icon={<VideoIcon sx={{ fontSize: 14 }} />} label="Platform" />
          <FormControl fullWidth size="small" sx={fieldSx}>
            <Select name="platform" value={form.platform} onChange={handleChange}>
              {PLATFORMS.map((p) => (
                <MenuItem key={p} value={p} sx={{ fontSize: 14 }}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <FieldLabel icon={<LinkIcon sx={{ fontSize: 14 }} />} label="Meeting Link" />
          <TextField
            name="meetingLink" value={form.meetingLink} onChange={handleChange}
            placeholder="https://..."
            fullWidth size="small" sx={fieldSx}
          />
        </Box>

        <Box>
          <FieldLabel icon={<NotesIcon sx={{ fontSize: 14 }} />} label="Purpose *" />
          <TextField
            name="purpose" value={form.purpose} onChange={handleChange}
            multiline rows={3}
            placeholder="What will you cover in this session?"
            fullWidth size="small" sx={fieldSx}
          />
        </Box>

        <Box sx={{
          p: 1.5, borderRadius: 2,
          bgcolor: C.primarySofter,
          border: `1px solid ${C.primarySoft}`,
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.notifyMentor} onChange={handleChange}
                name="notifyMentor" size="small"
                sx={{
                  "& .MuiSwitch-thumb": { boxShadow: "none" },
                  "& .Mui-checked": { color: `${C.primary} !important` },
                  "& .Mui-checked + .MuiSwitch-track": { bgcolor: `${C.primary} !important` },
                }}
              />
            }
            label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <NotifyIcon sx={{ fontSize: 16, color: C.primary }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
                  Send notification email
                </Typography>
              </Stack>
            }
            sx={{ m: 0 }}
          />
        </Box>

        <Button
          variant="contained" onClick={handleSubmit} disabled={loading}
          startIcon={!loading && <Add sx={{ fontSize: 18 }} />}
          sx={{
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            color: "#fff",
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 700, fontSize: 14,
            py: 1.5, boxShadow: "none",
            "&:hover": { boxShadow: `0 4px 14px ${C.primary}40` },
            "&.Mui-disabled": { background: "#CCC", color: "#fff" },
          }}
        >
          {loading
            ? <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} sx={{ color: "#fff" }} />
                <span>Creating…</span>
              </Stack>
            : "Create Session"}
        </Button>
      </Stack>
    </Box>
  );
}

function FieldLabel({ icon, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
      <Box sx={{ color: C.textMuted, display: "flex" }}>{icon}</Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.textSecondary }}>
        {label}
      </Typography>
    </Stack>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
const ViewSessions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const user = JSON.parse(localStorage.getItem("user") || "null");
  const isMentor = user?.role === "mentor";
  const mentorId = isMentor ? (user.id || user._id) : null;

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [, setNowTick] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { fetchSessions(); }, [mentorId]);

  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError("");
      // Mentor mode: filter by mentorId. Learner mode: fetch all.
      const url = isMentor
        ? `${API}/api/sessions?mentorId=${mentorId}`
        : `${API}/api/sessions`;
      const res = await axios.get(url);
      setSessions(res.data.data || res.data || []);
      setPage(1);
    } catch (err) {
      setError("Failed to load scheduled sessions.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-KE", {
      year: "numeric", month: "long", day: "numeric",
    });
  };

  const upcomingSessions = useMemo(
    () => sessions.filter(isSessionStillActive),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessions]
  );

  const totalSessions = upcomingSessions.length;
  const totalPages = Math.max(1, Math.ceil(totalSessions / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const visibleSessions = useMemo(() => {
    const start = (page - 1) * pageSize;
    return upcomingSessions.slice(start, start + pageSize);
  }, [upcomingSessions, page, pageSize]);

  const rangeStart = totalSessions === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalSessions);

  const handlePageChange = (_e, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCreated = () => {
    setDrawerOpen(false);
    fetchSessions();
  };

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <TopBar />

      <Box sx={{
        flex: 1,
        px: { xs: 1.5, sm: 3, md: 5, lg: 8 },
        py: { xs: 3, md: 4 },
        width: "100%", boxSizing: "border-box",
      }}>
        {/* ── Hero ─────────────────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative", borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 },
            mb: { xs: 2.5, sm: 3 },
            color: "#fff",
            boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute", top: "-40%", right: "-10%",
            width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}
            sx={{ position: "relative", zIndex: 1 }}>
            <Box flex={1} minWidth={0}>
              <Typography sx={{
                fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                textTransform: "uppercase", color: "rgba(255,255,255,0.7)", mb: 0.75,
              }}>
                {isMentor ? "Mentor Console" : "Mentorship"}
              </Typography>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
                color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
              }}>
                {isMentor ? "My Sessions" : "Scheduled Sessions"}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: { xs: 13, sm: 14 }, maxWidth: 540, lineHeight: 1.5 }}>
                {isMentor
                  ? "Schedule and track the sessions you've created for learners"
                  : "Browse upcoming mentorship sessions and join when they go live"}
              </Typography>
            </Box>

            {isMentor && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setDrawerOpen(true)}
                sx={{
                  bgcolor: "#fff", color: C.primary,
                  borderRadius: 2.5, fontWeight: 700, fontSize: 13,
                  textTransform: "none",
                  px: 2.5, py: 1.25,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                  "&:hover": { bgcolor: "#fff", boxShadow: "0 6px 18px rgba(0,0,0,0.2)" },
                  alignSelf: "flex-start",
                }}
              >
                New Session
              </Button>
            )}
          </Stack>
        </Paper>

        {/* ── Sessions card ───────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3, overflow: "hidden",
            border: `1px solid ${C.border}`,
            bgcolor: C.paper,
            p: { xs: 2, sm: 2.5, md: 3 },
            boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: "3px",
              background: `linear-gradient(90deg, ${C.primary}, ${C.primary}88)`,
            },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
            mb={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: 15, sm: 16 }, color: C.textPrimary }}>
                Upcoming Sessions
              </Typography>
              <Chip
                label={totalSessions}
                size="small"
                sx={{
                  bgcolor: C.primarySoft, color: C.primary,
                  fontWeight: 700, height: 22,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1.5}>
              <IconButton size="small" onClick={fetchSessions}
                sx={{ color: C.primary, "&:hover": { bgcolor: C.primarySoft } }}>
                <Refresh sx={{ fontSize: 18 }} />
              </IconButton>
              {totalSessions > PAGE_SIZE_OPTIONS[0] && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: 12, color: C.textMuted, fontWeight: 600 }}>
                    Per page:
                  </Typography>
                  <FormControl size="small">
                    <Select
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                      sx={{
                        fontSize: 13, borderRadius: 2,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: C.border },
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>{n}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2.5, borderColor: C.borderSoft }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress sx={{ color: C.primary }} />
            </Box>
          ) : totalSessions === 0 ? (
            <Stack alignItems="center" spacing={1.5} py={5}>
              <EventNote sx={{ fontSize: 48, color: C.textMuted, opacity: 0.5 }} />
              <Typography sx={{ fontWeight: 700, color: C.textPrimary, fontSize: 14 }}>
                No upcoming sessions
              </Typography>
              <Typography sx={{ fontSize: 12, color: C.textSecondary, textAlign: "center", maxWidth: 320 }}>
                {isMentor
                  ? "Create your first session by clicking 'New Session' above"
                  : "There are no scheduled sessions at the moment. Check back soon."}
              </Typography>
              {isMentor && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setDrawerOpen(true)}
                  sx={{
                    bgcolor: C.primary, color: "#fff",
                    borderRadius: 2.5, py: 1, px: 2.5, mt: 1,
                    fontWeight: 700, fontSize: 13,
                    textTransform: "none", boxShadow: "none",
                    "&:hover": { bgcolor: C.primaryDark, boxShadow: `0 4px 12px ${C.primary}30` },
                  }}
                >
                  Create First Session
                </Button>
              )}
            </Stack>
          ) : (
            <Box>
              {visibleSessions.map((session) => (
                <Paper
                  key={session._id}
                  elevation={0}
                  sx={{
                    position: "relative",
                    p: { xs: 2, sm: 2.5 },
                    mb: 2,
                    borderRadius: 2.5,
                    border: `1px solid ${C.border}`,
                    bgcolor: C.paper,
                    transition: "all 0.2s ease",
                    width: "100%", boxSizing: "border-box",
                    overflow: "hidden",
                    "&:hover": {
                      borderColor: C.primary,
                      boxShadow: `0 4px 16px ${C.primary}15`,
                      transform: "translateY(-1px)",
                    },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0, left: 0, bottom: 0,
                      width: "3px",
                      background: `linear-gradient(180deg, ${C.primary}, ${C.primaryLight})`,
                    },
                  }}
                >
                  <Box sx={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 1, sm: 2 },
                    mb: 1.5,
                  }}>
                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.95rem", sm: "1.05rem" },
                      color: C.textPrimary, lineHeight: 1.4,
                    }}>
                      {session.title || "Untitled Session"}
                    </Typography>

                    <Chip
                      icon={<VideoIcon sx={{ fontSize: "14px !important", color: `${C.primary} !important` }} />}
                      label={session.platform || "Session"}
                      size="small"
                      sx={{
                        flexShrink: 0,
                        bgcolor: C.primarySoft, color: C.primary,
                        fontWeight: 700, fontSize: 11,
                        height: 24,
                        "& .MuiChip-icon": { color: C.primary, ml: 0.75 },
                      }}
                    />
                  </Box>

                  {/* Mentor row — hidden in mentor mode (they know it's theirs) */}
                  {!isMentor && (
                    <Stack direction="row" alignItems="center" spacing={0.75} mb={1.75} flexWrap="wrap" useFlexGap>
                      <PersonOutline sx={{ fontSize: 16, color: C.primary }} />
                      <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: C.textPrimary, fontWeight: 600 }}>
                        {session.mentorName || "Unassigned"}
                      </Typography>
                      {session.mentorEmail && (
                        <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: C.textMuted }}>
                          · {session.mentorEmail}
                        </Typography>
                      )}
                    </Stack>
                  )}

                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mb={1.5}>
                    <Chip
                      icon={<CalendarToday sx={{ fontSize: "13px !important", color: `${C.textSecondary} !important` }} />}
                      label={formatDate(session.date)}
                      size="small"
                      sx={{
                        bgcolor: C.borderSoft, color: C.textSecondary,
                        fontWeight: 600, fontSize: 11, height: 24,
                        "& .MuiChip-icon": { color: C.textSecondary },
                      }}
                    />
                    <Chip
                      icon={<AccessTime sx={{ fontSize: "13px !important", color: `${C.textSecondary} !important` }} />}
                      label={`${session.startTime || "--"} - ${session.endTime || "--"}`}
                      size="small"
                      sx={{
                        bgcolor: C.borderSoft, color: C.textSecondary,
                        fontWeight: 600, fontSize: 11, height: 24,
                        "& .MuiChip-icon": { color: C.textSecondary },
                      }}
                    />
                    {session.duration && (
                      <Chip
                        label={session.duration}
                        size="small"
                        sx={{
                          bgcolor: C.secondarySoft, color: C.secondary,
                          fontWeight: 600, fontSize: 11, height: 24,
                        }}
                      />
                    )}
                  </Stack>

                  <Typography sx={{
                    mb: 1.75,
                    fontSize: { xs: 12.5, sm: 13.5 },
                    color: C.textSecondary,
                    lineHeight: 1.6,
                  }}>
                    <Box component="span" sx={{ fontWeight: 700, color: C.textPrimary }}>Purpose:</Box>{" "}
                    {session.purpose || "N/A"}
                  </Typography>

                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 1.5, sm: 2 },
                  }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <NotifyIcon sx={{
                        fontSize: 14,
                        color: session.notifyMentor ? C.secondary : C.textMuted,
                      }} />
                      <Typography sx={{
                        fontSize: 12, color: C.textMuted, fontWeight: 600,
                      }}>
                        Notifications {session.notifyMentor ? "on" : "off"}
                      </Typography>
                    </Stack>

                    {session.meetingLink && (
                      <Button
                        variant="contained"
                        startIcon={<Launch sx={{ fontSize: 16 }} />}
                        href={session.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          bgcolor: C.primary,
                          color: "#fff",
                          textTransform: "none",
                          borderRadius: 2,
                          fontWeight: 700,
                          fontSize: 12.5,
                          px: 2.25, py: 0.85,
                          boxShadow: "none",
                          width: { xs: "100%", sm: "auto" },
                          "&:hover": { bgcolor: C.primaryDark, boxShadow: `0 4px 12px ${C.primary}30` },
                        }}
                      >
                        Join Meeting
                      </Button>
                    )}
                  </Box>
                </Paper>
              ))}

              {totalSessions > pageSize && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  mt={2.5}
                  pt={2.5}
                  sx={{ borderTop: `1px solid ${C.borderSoft}` }}
                >
                  <Typography sx={{ fontSize: 13, color: C.textSecondary }}>
                    Showing <Box component="span" sx={{ fontWeight: 700, color: C.textPrimary }}>{rangeStart}</Box>–
                    <Box component="span" sx={{ fontWeight: 700, color: C.textPrimary }}>{rangeEnd}</Box> of{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: C.textPrimary }}>{totalSessions}</Box>
                  </Typography>

                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    shape="rounded"
                    siblingCount={1}
                    boundaryCount={1}
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        fontSize: { xs: 12, sm: 13 },
                        fontWeight: 700,
                        color: C.textSecondary,
                        "&:hover": { bgcolor: C.primarySoft },
                      },
                      "& .Mui-selected": {
                        bgcolor: `${C.primary} !important`,
                        color: "#fff !important",
                        "&:hover": { bgcolor: `${C.primaryDark} !important` },
                      },
                    }}
                  />
                </Stack>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      {/* ── Create-session drawer (mentor only) ─────────────────────────── */}
      {isMentor && (
        <Drawer
          anchor={isMobile ? "bottom" : "right"}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: "100%", md: 440 },
              maxHeight: { xs: "92vh", md: "100vh" },
              borderTopLeftRadius: { xs: 20, md: 0 },
              borderTopRightRadius: { xs: 20, md: 0 },
              p: 0,
            },
          }}
        >
          {isMobile && (
            <Box sx={{
              width: 40, height: 4,
              bgcolor: C.borderSoft, borderRadius: 2,
              mx: "auto", mt: 1.5, mb: 0.5,
            }} />
          )}
          <Box sx={{ p: { xs: 2.5, md: 3 }, overflow: "auto" }}>
            <CreateSessionForm
              mentorId={mentorId}
              onCreated={handleCreated}
              onCancel={() => setDrawerOpen(false)}
            />
          </Box>
        </Drawer>
      )}

      <Footer />
    </Box>
  );
};

export default ViewSessions;